/**
 * WorkflowDesignerView — editor de un Approval Workflow Template (OT-0055;
 * antes visor, Doc 21 Fase 1.h).
 *
 * Comparte con los demás editores la cabecera (metadata, metodología,
 * autosave, acciones) y el banner de solo lectura, que monta
 * `TemplateDetailView`.
 *
 * Edita la forma real que valida el backend (`WorkflowDefinition`):
 *   - Por idioma: nombre y descripción del flujo.
 *   - Etapas (1..10, `order` consecutivo desde 1 — se renumera al mover o
 *     quitar): nombre en es y en, roles requeridos (1..10), aprobaciones
 *     (`all` o N), `exclude_self`, `allow_skip_if_no_role_assignee`.
 *   - `allow_user_override`.
 *
 * Roles ofrecidos: los pseudo-roles del proyecto y los del catálogo que la
 * completitud acepta para este flujo (`citableTemplates`): los de su
 * metodología y los genéricos (un flujo genérico, solo genéricos).
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, GitBranch, Plus, Trash2, Users, X } from 'lucide-react'

import {
  listTemplates,
  type ApprovalWorkflowDefinition,
  type Template,
  type TemplateVersionWithDefinition,
  type WorkflowHeaderI18n,
  type WorkflowStage,
  type WorkflowStageI18n,
} from '../../../api/templates'
import { useAccessToken } from '../../../auth/useAccessToken'
import type { TenantManageBlock } from '../../../lib/tiers'
import { useDefinitionAutosave, type AutosaveState } from './useDefinitionAutosave'
import {
  findWorkflowIssues,
  isPseudoRole,
  MAX_ROLES,
  MAX_STAGES,
  PSEUDO_ROLE_KEYS,
} from './workflowRules'
import { citableTemplates } from '../../../lib/methodologyScope'
import {
  DEFAULT_LOCALE,
  editingLocale,
  pickLocalized,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '../../../i18n/config'

type Locale = SupportedLocale

interface WorkflowDesignerViewProps {
  template: Template
  version: TemplateVersionWithDefinition
  manageBlock: TenantManageBlock | null
  renderHeader: (autosave: AutosaveState) => React.ReactNode
  renderReadOnlyBanner: () => React.ReactNode
}

export function WorkflowDesignerView({
  template,
  version,
  manageBlock,
  renderHeader,
  renderReadOnlyBanner,
}: WorkflowDesignerViewProps) {
  const { t, i18n } = useTranslation('templates')
  const { accessToken } = useAccessToken()
  const readOnly =
    template.is_system_default || version.published_at !== null || manageBlock !== null

  const savedDefinition = version.definition as unknown as ApprovalWorkflowDefinition
  const [definition, setDefinition] = useState<ApprovalWorkflowDefinition>(savedDefinition)
  const locales = SUPPORTED_LOCALES.filter((loc) => definition.i18n[loc])
  const [activeLocale, setActiveLocale] = useState<Locale>(() =>
    editingLocale(locales, i18n.language),
  )

  const autosave = useDefinitionAutosave({
    templateId: template.id,
    versionId: version.id,
    definition,
    savedDefinition,
    readOnly,
    preflight: findWorkflowIssues,
  })

  const rolesQuery = useQuery({
    queryKey: ['templates', 'list', 'role'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listTemplates(accessToken, { template_type: 'role' })
    },
    enabled: !!accessToken,
    staleTime: 60_000,
  })
  const citableRoles = useMemo(
    () => citableTemplates(rolesQuery.data ?? [], template.methodology_id),
    [rolesQuery.data, template.methodology_id],
  )
  const roleOptions = useMemo(
    () =>
      [...citableRoles.values()].sort((a, b) =>
        a.name.localeCompare(b.name, i18n.language || DEFAULT_LOCALE),
      ),
    [citableRoles, i18n.language],
  )
  const roleLabel = (key: string): string =>
    isPseudoRole(key)
      ? t(`designer.workflow.pseudo_roles.${key}`)
      : (citableRoles.get(key)?.name ??
        rolesQuery.data?.find((r) => r.template_key === key)?.name ??
        key)

  const block = definition.i18n[activeLocale]
  const missingLocales = SUPPORTED_LOCALES.filter((loc) => !definition.i18n[loc])
  const stages = definition.stages

  const updateBlock = (patch: Partial<WorkflowHeaderI18n>) =>
    setDefinition((d) => ({
      ...d,
      i18n: {
        ...d.i18n,
        [activeLocale]: { ...d.i18n[activeLocale], ...patch } as WorkflowHeaderI18n,
      },
    }))
  const addLocale = (loc: Locale) => {
    setDefinition((d) => ({
      ...d,
      i18n: { ...d.i18n, [loc]: { name: d.i18n[activeLocale]?.name ?? '' } },
    }))
    setActiveLocale(loc)
  }
  const setStages = (next: WorkflowStage[]) =>
    setDefinition((d) => ({ ...d, stages: next.map((s, i) => ({ ...s, order: i + 1 })) }))
  const updateStage = (index: number, patch: Partial<WorkflowStage>) =>
    setStages(stages.map((s, i) => (i === index ? { ...s, ...patch } : s)))
  const moveStage = (index: number, delta: -1 | 1) => {
    const next = [...stages]
    const [moved] = next.splice(index, 1)
    if (moved) next.splice(index + delta, 0, moved)
    setStages(next)
  }
  const addStage = () =>
    setStages([
      ...stages,
      {
        order: stages.length + 1,
        name: {},
        mode: 'parallel',
        required_roles: [],
        min_approvals: 'all',
      },
    ])

  return (
    <div className="h-full flex flex-col min-h-0">
      {renderHeader(autosave)}
      {readOnly ? renderReadOnlyBanner() : null}

      {/* Idioma en edición (nombre y descripción del flujo) */}
      <div className="bg-surface border-b border-line px-6 py-2 flex items-center justify-end gap-1.5">
        <span className="text-[10px] uppercase tracking-wide text-ink-subtle">
          {t('designer.global_config.editing_locale')}
        </span>
        {locales.map((loc) => (
          <button
            key={loc}
            type="button"
            onClick={() => setActiveLocale(loc)}
            aria-pressed={loc === activeLocale}
            className={
              loc === activeLocale
                ? 'px-2 py-0.5 text-xs font-mono uppercase rounded bg-brand-soft text-brand'
                : 'px-2 py-0.5 text-xs font-mono uppercase rounded hover:bg-surface-muted text-ink-muted'
            }
          >
            {loc}
          </button>
        ))}
        {!readOnly
          ? missingLocales.map((loc) => (
              <button
                key={loc}
                type="button"
                onClick={() => addLocale(loc)}
                title={t('designer.global_config.add_locale')}
                className="px-2 py-0.5 text-xs font-mono uppercase rounded border border-dashed border-line text-ink-subtle hover:text-ink hover:border-line-strong inline-flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                {loc}
              </button>
            ))
          : null}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto bg-canvas">
        <div className="max-w-3xl mx-auto py-8 px-6 space-y-8">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-ink-subtle">
            <GitBranch className="w-3.5 h-3.5" />
            <span>{t('designer.workflow.eyebrow')}</span>
          </div>

          {!block ? (
            <p className="text-center text-sm text-ink-subtle italic">
              {t('designer.workflow.no_locale_content')}
            </p>
          ) : (
            <section className="space-y-4">
              <label className="block">
                <span className="text-xs font-medium text-ink-muted">
                  {t('designer.workflow.name_label')}
                </span>
                <input
                  type="text"
                  value={block.name}
                  onChange={(e) => updateBlock({ name: e.target.value })}
                  disabled={readOnly}
                  maxLength={120}
                  lang={activeLocale}
                  className="mt-1 w-full bg-surface border border-line rounded-md px-3 py-2 font-display text-lg text-ink focus:outline-none focus:border-brand disabled:opacity-70"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-ink-muted">
                  {t('designer.workflow.description_label')}
                </span>
                <textarea
                  value={block.description ?? ''}
                  onChange={(e) => updateBlock({ description: e.target.value })}
                  disabled={readOnly}
                  maxLength={500}
                  rows={2}
                  lang={activeLocale}
                  className="mt-1 w-full bg-surface border border-line rounded-md px-3 py-2 font-body text-sm text-ink focus:outline-none focus:border-brand disabled:opacity-70 resize-y"
                />
              </label>
            </section>
          )}

          <section>
            <p className="text-xs font-medium text-ink-muted">
              {t('designer.workflow.stages_title')}
            </p>
            <p className="text-[11px] text-ink-subtle mb-3">{t('designer.workflow.stages_hint')}</p>
            <ol className="space-y-3">
              {stages.map((stage, index) => (
                <StageEditor
                  key={index}
                  stage={stage}
                  locale={activeLocale}
                  isFirst={index === 0}
                  isLast={index === stages.length - 1}
                  readOnly={readOnly}
                  roleOptions={roleOptions}
                  roleLabel={roleLabel}
                  isAllowedRole={(key) => isPseudoRole(key) || citableRoles.has(key)}
                  rolesLoaded={rolesQuery.isSuccess}
                  onChange={(patch) => updateStage(index, patch)}
                  onMove={(delta) => moveStage(index, delta)}
                  onRemove={() => setStages(stages.filter((_, i) => i !== index))}
                />
              ))}
            </ol>
            {stages.length === 0 ? (
              <p className="font-body text-sm text-ink-muted italic">
                {t('designer.workflow.no_stages')}
              </p>
            ) : null}
            {!readOnly && stages.length < MAX_STAGES ? (
              <button
                type="button"
                onClick={addStage}
                className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-dashed border-line px-3 py-1.5 text-xs text-ink-muted hover:text-ink hover:border-line-strong"
              >
                <Plus className="w-3.5 h-3.5" />
                {t('designer.workflow.add_stage')}
              </button>
            ) : null}
          </section>

          <label className="flex items-start gap-2 border-t border-line pt-6 text-sm text-ink">
            <input
              type="checkbox"
              checked={definition.allow_user_override}
              onChange={(e) =>
                setDefinition((d) => ({ ...d, allow_user_override: e.target.checked }))
              }
              disabled={readOnly}
              className="mt-1 accent-brand"
            />
            <span>
              {t('designer.workflow.user_override_label')}
              <span className="block text-[11px] text-ink-subtle">
                {definition.allow_user_override
                  ? t('designer.workflow.user_override_allowed')
                  : t('designer.workflow.user_override_disallowed')}
              </span>
            </span>
          </label>
        </div>
      </div>
    </div>
  )
}

// ─── Etapa ─────────────────────────────────────────────────────────────────
function StageEditor({
  stage,
  locale,
  isFirst,
  isLast,
  readOnly,
  roleOptions,
  roleLabel,
  isAllowedRole,
  rolesLoaded,
  onChange,
  onMove,
  onRemove,
}: {
  stage: WorkflowStage
  /** Idioma en edición del flujo: el nombre de la etapa se escribe en él. */
  locale: SupportedLocale
  isFirst: boolean
  isLast: boolean
  readOnly: boolean
  roleOptions: Template[]
  roleLabel: (key: string) => string
  isAllowedRole: (key: string) => boolean
  rolesLoaded: boolean
  onChange: (patch: Partial<WorkflowStage>) => void
  onMove: (delta: -1 | 1) => void
  onRemove: () => void
}) {
  const { t } = useTranslation('templates')
  const order = stage.order
  const chosen = new Set(stage.required_roles)
  const iconButton =
    'p-1 rounded text-ink-subtle hover:text-ink hover:bg-surface-muted disabled:opacity-30 disabled:hover:bg-transparent'
  const inputClass =
    'mt-1 w-full bg-canvas border border-line rounded-md px-2.5 py-1.5 text-sm text-ink focus:outline-none focus:border-brand disabled:opacity-70'

  return (
    <li className="rounded-md border border-line bg-surface px-4 py-3 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-mono uppercase tracking-wider text-ink-subtle">
          {t('designer.workflow.stage_label', { order })}
        </span>
        {!readOnly ? (
          <span className="ml-auto flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => onMove(-1)}
              disabled={isFirst}
              aria-label={t('designer.workflow.move_up', { order })}
              className={iconButton}
            >
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onMove(1)}
              disabled={isLast}
              aria-label={t('designer.workflow.move_down', { order })}
              className={iconButton}
            >
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={onRemove}
              aria-label={t('designer.workflow.remove_stage', { order })}
              className={`${iconButton} hover:text-critical`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </span>
        ) : null}
      </div>

      <label className="block">
        <span className="text-[11px] font-medium text-ink-muted">
          {t('designer.workflow.stage_name_label')}
        </span>
        <input
          type="text"
          value={stage.name[locale] ?? ''}
          onChange={(e) => onChange({ name: withLocaleValue(stage.name, locale, e.target.value) })}
          // Sin nombre en este idioma: se ve el de otro, que es el que
          // mostrará el sistema hasta que se traduzca.
          placeholder={pickLocalized(stage.name, locale) ?? ''}
          disabled={readOnly}
          maxLength={80}
          lang={locale}
          className={inputClass}
        />
      </label>

      <div>
        <p className="text-[11px] font-medium text-ink-muted">
          {t('designer.workflow.field_required_roles')}
        </p>
        <p className="text-[10px] text-ink-subtle mb-1.5">{t('designer.workflow.roles_hint')}</p>
        <div className="flex flex-wrap items-center gap-1.5">
          {stage.required_roles.map((key) => {
            const foreign = rolesLoaded && !isAllowedRole(key)
            return (
              <span
                key={key}
                title={foreign ? t('designer.workflow.role_foreign') : key}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] ${foreign ? 'bg-critical-soft text-critical' : 'bg-surface-muted text-ink'}`}
              >
                <Users className="w-3 h-3 opacity-70" />
                {roleLabel(key)}
                {!readOnly ? (
                  <button
                    type="button"
                    onClick={() =>
                      onChange({ required_roles: stage.required_roles.filter((r) => r !== key) })
                    }
                    aria-label={t('designer.workflow.remove_role', { role: roleLabel(key) })}
                    className="ml-0.5 rounded hover:bg-surface"
                  >
                    <X className="w-3 h-3" />
                  </button>
                ) : null}
              </span>
            )
          })}
          {!readOnly && stage.required_roles.length < MAX_ROLES ? (
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) {
                  onChange({ required_roles: [...stage.required_roles, e.target.value] })
                }
              }}
              aria-label={t('designer.workflow.add_role')}
              className="bg-canvas border border-dashed border-line rounded px-1.5 py-0.5 text-[11px] text-ink-muted focus:outline-none focus:border-brand"
            >
              <option value="">{t('designer.workflow.add_role')}</option>
              <optgroup label={t('designer.workflow.pseudo_group')}>
                {PSEUDO_ROLE_KEYS.filter((k) => !chosen.has(k)).map((k) => (
                  <option key={k} value={k}>
                    {roleLabel(k)}
                  </option>
                ))}
              </optgroup>
              <optgroup label={t('designer.workflow.catalog_group')}>
                {roleOptions
                  .filter((r) => !chosen.has(r.template_key))
                  .map((r) => (
                    <option key={r.template_key} value={r.template_key}>
                      {r.name}
                    </option>
                  ))}
              </optgroup>
            </select>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[200px_minmax(0,1fr)] gap-3 items-start">
        <label className="block">
          <span className="text-[11px] font-medium text-ink-muted">
            {t('designer.workflow.field_min_approvals')}
          </span>
          <select
            value={String(stage.min_approvals)}
            onChange={(e) =>
              onChange({
                min_approvals: e.target.value === 'all' ? 'all' : Number(e.target.value),
              })
            }
            disabled={readOnly}
            className={inputClass}
          >
            <option value="all">{t('designer.workflow.min_approvals_all')}</option>
            {Array.from({ length: MAX_ROLES }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {t('designer.workflow.min_approvals_n_option', { count: n })}
              </option>
            ))}
          </select>
        </label>
        <div className="space-y-2 pt-1">
          <StageToggle
            checked={stage.exclude_self ?? false}
            disabled={readOnly}
            label={t('designer.workflow.field_exclude_self')}
            hint={t('designer.workflow.exclude_self_hint')}
            onChange={(exclude_self) => onChange({ exclude_self })}
          />
          <StageToggle
            checked={stage.allow_skip_if_no_role_assignee ?? false}
            disabled={readOnly}
            label={t('designer.workflow.field_allow_skip')}
            hint={t('designer.workflow.allow_skip_hint')}
            onChange={(allow_skip_if_no_role_assignee) =>
              onChange({ allow_skip_if_no_role_assignee })
            }
          />
        </div>
      </div>
    </li>
  )
}

function StageToggle({
  checked,
  disabled,
  label,
  hint,
  onChange,
}: {
  checked: boolean
  disabled: boolean
  label: string
  hint: string
  onChange: (checked: boolean) => void
}) {
  return (
    <label className="flex items-start gap-2 text-xs text-ink">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="mt-0.5 accent-brand"
      />
      <span>
        {label}
        <span className="block text-[10px] text-ink-subtle">{hint}</span>
      </span>
    </label>
  )
}

/** El nombre por idioma con `value` en `locale`; vacío quita ese idioma (el
 * backend no acepta nombres vacíos y basta con uno). */
function withLocaleValue(
  name: WorkflowStageI18n,
  locale: SupportedLocale,
  value: string,
): WorkflowStageI18n {
  const next = { ...name }
  if (value) next[locale] = value
  else delete next[locale]
  return next
}
